/*
 * RTR DYO INTEGRATION CONTRACT - DO NOT MODIFY WITHOUT A DIRECT USER REQUEST.
 * RTR owns all renderer/integration functions. Do not add, replace, wrap or
 * override functions, event handlers, value mappings or vendor API calls.
 * Preserve the exact selector IDs, names, values, data attributes, DOM structure
 * and event contract, including rtr_metal, rtr_tex, rtr_shapes and rtr_carat.
 * Unrequested refactors or enhancements can break the RTR integration.
 * A direct request authorizes only its stated scope; preserve all other contracts.
 * See CLAUDE.md: RTR DYO integration protection.
 */
(() => {
  const SCRIPT_NAME = 'rtr-dyo.js';

  window.Eurus = window.Eurus || {};
  window.Eurus.loadedScript = window.Eurus.loadedScript || new Set();
  if (window.Eurus.loadedScript.has(SCRIPT_NAME)) return;
  window.Eurus.loadedScript.add(SCRIPT_NAME);

  // The bucket the vendor renders at 1.50 ct; the model caption only holds for it.
  const DEFAULT_MODEL_CARAT = '1.00 - 2.00 Ct';

  let mediaState = null;

  const normalize = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&amp;/g, '&')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const isPlatinum = (value) => {
    const metal = String(value || '').trim().toLowerCase();
    return metal.includes('platinum') || metal === 'pt';
  };

  const getDyoData = () => {
    try {
      return JSON.parse(window.localStorage.getItem('dyo_data') || 'null') || {};
    } catch (error) {
      return {};
    }
  };

  const setDyoData = (data) => {
    try {
      window.localStorage.setItem('dyo_data', JSON.stringify(data));
      if (window.Alpine?.store?.('xDYO')) {
        window.Alpine.store('xDYO').dyoData = data;
      }
    } catch (error) {
      // Local storage can be unavailable in some browser privacy modes.
    }
  };

  const updateStoredSetting = (values) => {
    const data = getDyoData();
    if (!data.process?.setting) return;

    data.process = data.process || {};
    data.process.setting = data.process.setting || {};
    Object.assign(data.process.setting, values);
    setDyoData(data);
  };

  const getStoredSetting = () => {
    const storedSetting = getDyoData()?.process?.setting;
    const liveSetting = window.Alpine?.store?.('xDYO')?.dyoData?.process?.setting;
    return storedSetting || liveSetting || {};
  };

  const collectSelections = (root = document) => {
    const selections = {};

    root.querySelectorAll('[data-rtr-metal-type]').forEach((input) => {
      if (!input.checked || !input.value) return;
      selections['RTR Metal Type'] = input.value;
    });

    root.querySelectorAll('[data-rtr-dyo-property]').forEach((input) => {
      if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) return;
      if (!input.value) return;
      selections[input.dataset.rtrDyoProperty] = input.value;
    });

    return selections;
  };

  const persistSelections = (root) => {
    const currentSelections = getStoredSetting().rtrSelections || {};
    const selections = collectSelections(root);
    const nextSelections = {
      ...currentSelections,
      ...selections
    };
    if (isPlatinum(selections['RTR Metal Type'])) delete nextSelections['RTR Shank Color'];
    updateStoredSetting({
      rtrSelections: nextSelections
    });
  };

  const getStoredSelections = () => {
    const selections = getStoredSetting().rtrSelections;
    return selections && typeof selections === 'object' ? selections : {};
  };

  const findMatchingInput = (root, selector, value) => Array.from(root.querySelectorAll(selector))
    .find((input) => normalize(input.value) === normalize(value));

  const applyStoredSelections = (root, selections, keys = null) => {
    if (!selections || typeof selections !== 'object') return false;

    let changed = false;
    Object.entries(selections).forEach(([property, value]) => {
      if (!value || (keys && !keys.includes(property))) return;

      const selector = property === 'RTR Metal Type'
        ? '[data-rtr-metal-type]'
        : `[data-rtr-dyo-property="${CSS.escape(property)}"]`;
      const input = findMatchingInput(root, selector, value);
      if (!input || input.checked) return;

      input.checked = true;
      changed = true;
    });

    return changed;
  };

  const setProxy = (root, role, value) => {
    const proxy = root.querySelector(`[data-rtr-proxy="${role}"]`);
    if (!proxy || !value) return;

    let input = Array.from(proxy.querySelectorAll('input')).find((candidate) => (
      normalize(candidate.value) === normalize(value)
    ));

    if (!input) {
      input = document.createElement('input');
      input.type = 'radio';
      input.name = `${proxy.id}-proxy`;
      input.disabled = true;
      proxy.appendChild(input);
    }

    input.value = value;
    proxy.querySelectorAll('input').forEach((candidate) => {
      candidate.checked = candidate === input;
    });
  };

  const getColorsForMetal = (root, metalValue) => {
    const raw = String(metalValue || '').toLowerCase().trim();
    if (isPlatinum(raw)) {
      const platinum = root.dataset.rtrPlatinum || 'Platinum';
      const yellowGold = root.dataset.rtrYellowGold || 'Yellow Gold';
      const roseGold = root.dataset.rtrRoseGold || 'Rose Gold';
      return [
        { label: platinum, value: 'Platinum' },
        { label: `18K ${yellowGold}`, value: '18K Yellow Gold', showKarat: true },
        { label: `18K ${roseGold}`, value: '18K Rose Gold', showKarat: true }
      ];
    }

    const karat = raw.match(/\b(9|10|14|18|21|22|24)\s*k\b/)?.[1];
    const prefix = karat ? `${karat}K ` : '';
    if (raw.includes('gold')) {
      return [
        { tone: 'White', label: root.dataset.rtrWhiteGold || 'White Gold' },
        { tone: 'Yellow', label: root.dataset.rtrYellowGold || 'Yellow Gold' },
        { tone: 'Rose', label: root.dataset.rtrRoseGold || 'Rose Gold' }
      ].map((metal) => ({
        label: `${prefix}${metal.label}`,
        value: `${prefix}${metal.tone} Gold`
      }));
    }

    if (metalValue) return [{ label: metalValue, value: metalValue }];
    return [
      { label: root.dataset.rtrWhiteGold || 'White Gold', value: 'White Gold' },
      { label: root.dataset.rtrYellowGold || 'Yellow Gold', value: 'Yellow Gold' },
      { label: root.dataset.rtrRoseGold || 'Rose Gold', value: 'Rose Gold' },
      { label: root.dataset.rtrPlatinum || 'Platinum', value: 'Platinum' }
    ];
  };

  const renderMetalColors = (root, role, colors, displayOnly = false) => {
    const container = root.querySelector(`[data-rtr-color-options="${role}"]`);
    if (!container) return;

    const previous = normalize(container.querySelector('input:checked')?.value);
    const previousTone = ['white', 'yellow', 'rose'].find((tone) => previous.includes(tone));
    const selectedIndex = Math.max(0, colors.findIndex((color) => {
      const current = normalize(color.value);
      return current === previous || (previousTone && current.includes(previousTone));
    }));
    const property = role === 'shank' ? 'RTR Shank Color' : 'RTR Head Color';
    const formId = root.dataset.productFormId;
    const metalImages = {
      'white-gold': root.dataset.rtrImgWhiteGold,
      'yellow-gold': root.dataset.rtrImgYellowGold,
      'rose-gold': root.dataset.rtrImgRoseGold,
      platinum: root.dataset.rtrImgPlatinum
    };
    const fragment = document.createDocumentFragment();

    colors.forEach((color, index) => {
      const id = `${root.id}-${role}-${index}`;
      const input = document.createElement('input');
      input.id = id;
      input.className = 'rtr-dyo-selectors__input';
      input.type = 'radio';
      input.value = color.value;
      if (displayOnly) {
        // Shown, not chosen: without a name, property or role it stays out of
        // the cart, the saved selections and the values the renderer reads.
        input.disabled = true;
        input.dataset.rtrDisplayOnly = '';
      } else {
        input.name = `properties[${property}]`;
        input.dataset.rtrDyoProperty = property;
        input.dataset.rtrMetalRole = role;
        input.setAttribute('form', formId);
      }
      input.checked = index === selectedIndex;

      input.dataset.rtrLabel = color.label;

      const label = document.createElement('label');
      label.className = 'rtr-dyo-selectors__swatch';
      label.htmlFor = id;

      const dot = document.createElement('span');
      dot.className = 'rtr-dyo-selectors__dot';
      const toneHandle = normalize(color.value);
      const tone = ['white', 'yellow', 'rose', 'platinum'].find((entry) => toneHandle.includes(entry));
      if (tone) {
        const swatch = tone === 'platinum' ? 'platinum' : `${tone}-gold`;
        dot.dataset.swatch = swatch;
        const image = metalImages[swatch];
        if (image) dot.style.setProperty('--rtr-swatch-img', `url('${image}')`);
      }

      const caption = document.createElement('span');
      caption.className = 'rtr-dyo-selectors__swatch-caption';
      caption.textContent = color.showKarat
        ? color.label
        : color.label.replace(/^\d+\s*K\s*/i, '');

      label.append(dot, caption);
      fragment.append(input, label);
    });

    container.replaceChildren(fragment);
  };

  const updateMetalColorAvailability = (root, preferredRole = 'shank') => {
    const groups = Object.fromEntries(['shank', 'head'].map((role) => [
      role, Array.from(root.querySelectorAll(`[data-rtr-metal-role="${role}"]`))
    ]));
    const tone = (input) => normalize(input?.value).match(/yellow|rose/)?.[0];
    const conflicts = (first, second) => tone(first) && tone(second) && tone(first) !== tone(second);
    const otherRole = preferredRole === 'head' ? 'shank' : 'head';
    const preferred = groups[preferredRole].find((input) => input.checked);
    const other = groups[otherRole].find((input) => input.checked);

    // Repair older saved combinations before syncing properties or the renderer.
    if (conflicts(preferred, other)) {
      const fallback = groups[otherRole].find((input) => !conflicts(preferred, input));
      if (fallback) {
        other.checked = false;
        fallback.checked = true;
      }
    }

    Object.entries(groups).forEach(([role, inputs]) => {
      const opposite = groups[role === 'shank' ? 'head' : 'shank'].find((input) => input.checked);
      inputs.forEach((input) => {
        const unavailable = !!conflicts(input, opposite);
        input.disabled = unavailable;
        const label = input.nextElementSibling;
        if (label) label.style.display = unavailable ? 'none' : '';
      });
    });
  };

  // Mirror the chosen colour swatch into the accordion header so the current
  // build reads at a glance while the panel is collapsed. Reuses the swatch
  // dot's own CSS (the [data-swatch] gradients + --rtr-swatch-img image) by
  // borrowing its class, so no styling is duplicated here.
  const updateGroupToken = (token, checked) => {
    const dot = checked?.nextElementSibling?.querySelector('.rtr-dyo-selectors__dot');
    if (!dot) {
      token.hidden = true;
      return;
    }
    token.className = 'rtr-acc__token rtr-dyo-selectors__dot';
    token.dataset.swatch = dot.dataset.swatch || '';
    const image = dot.style.getPropertyValue('--rtr-swatch-img');
    if (image) token.style.setProperty('--rtr-swatch-img', image);
    else token.style.removeProperty('--rtr-swatch-img');
    token.hidden = false;
  };

  const updateGroupLegends = (root) => {
    // The live value/token live in the accordion header, outside the fieldset,
    // so resolve the checked input from the accordion wrapper.
    root.querySelectorAll('[data-rtr-acc], [data-rtr-metal-group]').forEach((acc) => {
      const slot = acc.querySelector('[data-rtr-group-value]');
      if (!slot) return;
      const checked = acc.querySelector('input[data-rtr-dyo-property]:checked, input[data-rtr-metal-type]:checked, input[data-rtr-display-only]:checked');
      slot.textContent = checked ? checked.dataset.rtrLabel || checked.value : '';
      const token = acc.querySelector('[data-rtr-group-token]');
      if (token) updateGroupToken(token, checked);
    });
  };

  const syncStoneColorTab = (root) => {
    const checkedStoneColor = root.querySelector('[data-rtr-stone-color]:checked');
    const panel = checkedStoneColor?.closest('[data-rtr-stone-panel]');
    const panelName = panel?.dataset.rtrStonePanel;
    if (!panelName) return;

    const tab = root.querySelector(`#${CSS.escape(root.dataset.sectionId)}-rtr-stone-tab-${panelName === 'gem' ? 'gem' : 'fancy'}`);
    if (tab) tab.checked = true;
  };

  const syncVariantOption = (root, handle, value) => {
    if (!handle || !value) return;
    const optionRoot = document.querySelector(
      `#variant-update-${CSS.escape(root.dataset.sectionId)} [data-option-name="${CSS.escape(handle)}"]`
    );
    if (!optionRoot) return;

    const select = optionRoot.querySelector('select');
    if (select) {
      const option = Array.from(select.options).find((item) => (
        normalize(item.value) === normalize(value)
        || normalize(item.textContent) === normalize(value)
      ));
      if (option && select.value !== option.value) {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const radio = Array.from(optionRoot.querySelectorAll('input[type="radio"]'))
      .find((item) => normalize(item.value) === normalize(value));
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const metalOptionRoot = (root) => {
    const handle = root.dataset.metalOptionHandle;
    if (!handle) return null;
    return document.querySelector(
      `#variant-update-${CSS.escape(root.dataset.sectionId)} [data-option-name="${CSS.escape(handle)}"]`
    );
  };

  const hasDyoStoneWeight = () => {
    try {
      const savedData = JSON.parse(window.localStorage.getItem('dyo_data') || 'null');
      if (savedData?.process?.stone?.metafields?.weight) return true;
    } catch (error) {
      // Local storage can be unavailable in some browser privacy modes.
    }

    try {
      return !!window.Alpine?.store?.('xDYO')?.dyoData?.process?.stone?.metafields?.weight;
    } catch (error) {
      return false;
    }
  };

  const syncMetalTypeSelection = (root) => {
    const optionRoot = metalOptionRoot(root);
    const current = optionRoot?.querySelector('select')?.value
      || optionRoot?.querySelector('input[type="radio"]:checked')?.value
      || '';
    if (!current) return;

    let matched = false;
    root.querySelectorAll('[data-rtr-metal-type]').forEach((radio) => {
      const isMatch = normalize(radio.value) === normalize(current);
      if (isMatch) matched = true;
      radio.checked = isMatch;
    });
    if (matched) updateGroupLegends(root);
  };

  const syncSelectorMarkup = (root) => {
    const selections = collectSelections(root);
    const metalRole = root.dataset.rtrRenderMetalRole || 'head';
    const preferredMetal = metalRole === 'shank' ? 'RTR Shank Color' : 'RTR Head Color';
    const fallbackMetal = metalRole === 'shank' ? 'RTR Head Color' : 'RTR Shank Color';
    setProxy(root, 'metal', selections[preferredMetal] || selections[fallbackMetal]);
    setProxy(root, 'texture', selections['RTR Stone Color']);
    setProxy(root, 'shape', selections['RTR Stone Shape']);
    setProxy(root, 'carat', selections['RTR Carat Size']);

    const legacyValues = {
      shank: selections['RTR Shank Color'] || selections['RTR Head Color'] || '',
      head: selections['RTR Head Color'] || selections['RTR Shank Color'] || '',
      carat: selections['RTR Carat Size'] || ''
    };
    root.querySelectorAll('[data-rtr-legacy-summary]').forEach((summary) => {
      summary.querySelector('[data-rtr-legacy-value]').textContent = legacyValues[summary.dataset.rtrLegacySummary] || '';
    });
  };

  const updateHeadForStone = (root, stoneColor) => {
    if (root.dataset.rtrHeadTouched === 'true') return;
    const tone = {
      'yellow-diamond': 'yellow',
      'orange-diamond': 'yellow',
      'champagne-diamond': 'yellow',
      'pink-diamond': 'rose',
      'argyle-pink-diamond': 'rose',
      'red-diamond': 'rose',
      ruby: 'rose'
    }[normalize(stoneColor)];
    if (!tone) return;

    const input = Array.from(root.querySelectorAll('[data-rtr-metal-role="head"]'))
      .find((candidate) => normalize(candidate.value).includes(tone));
    if (!input || input.checked || input.disabled) return;

    input.checked = true;
    root.dataset.rtrAutoHead = 'true';
    input.dispatchEvent(new Event('change', { bubbles: true }));
    delete root.dataset.rtrAutoHead;
  };

  const initCaratSlider = (root) => {
    const container = root.querySelector('[data-rtr-carat]');
    const slider = root.querySelector('[data-rtr-carat-slider]');
    if (!container || !slider) return null;

    const radios = Array.from(root.querySelectorAll('input[data-rtr-dyo-property="RTR Carat Size"]'));
    const display = root.querySelector('[data-rtr-carat-display]');
    const labels = Array.from(container.querySelectorAll('[data-rtr-carat-to]'));
    const max = Math.max(1, radios.length - 1);
    const rtl = !!container.closest('.rtl');

    // Carat values are discrete, but a step-per-carat range only has a handful
    // of stops, so dragging snaps between them and feels notchy. We give the
    // native input a much finer grid (SUBSTEPS sub-positions per carat) so the
    // thumb glides 1:1 under the finger, then snap to the nearest carat when the
    // value settles. The sub-step grid is invisible to keyboard/AT (handled below).
    const SUBSTEPS = 100;
    const fineMax = max * SUBSTEPS;
    const clampIndex = (i) => Math.min(max, Math.max(0, i));
    const indexFromRaw = () => clampIndex(Math.round(Number(slider.value) / SUBSTEPS));

    const checkedIndex = radios.findIndex((radio) => radio.checked);
    const startIndex = clampIndex(checkedIndex >= 0 ? checkedIndex : Math.round(Number(slider.value)));
    slider.min = '0';
    slider.max = String(fineMax);
    slider.step = '1';
    slider.value = String(startIndex * SUBSTEPS);

    // Cheap: runs on every drag frame. Glides the thumb (one compositor custom
    // property) and, only when we cross into a new carat, refreshes the active
    // label + read-out. No selection / variant / 3D work happens here.
    //
    // While dragging the thumb tracks the raw pointer 1:1; otherwise it rests on
    // the nearest carat. Painting the snapped position when not dragging makes a
    // track tap glide straight to its carat (no detour out to the raw point and
    // back) and stops sub-carat pointer jitter from twitching the thumb.
    let dragging = false;
    let activeIndex = -1;
    const paint = () => {
      const p = dragging ? Number(slider.value) / fineMax : indexFromRaw() / max;
      container.style.setProperty('--rtr-carat-p', `${p}`);
      const index = indexFromRaw();
      if (index === activeIndex) return;
      activeIndex = index;
      labels.forEach((label, i) => label.classList.toggle('is-active', i === index));
      const radio = radios[index];
      if (!radio) return;
      const label = radio.dataset.rtrLabel || radio.value;
      if (display) display.textContent = label;
      slider.setAttribute('aria-valuetext', label);
    };
    let paintFrame = 0;
    const paintNow = () => {
      if (paintFrame) {
        window.cancelAnimationFrame(paintFrame);
        paintFrame = 0;
      }
      paint();
    };
    const schedulePaint = () => {
      if (paintFrame) return;
      paintFrame = window.requestAnimationFrame(() => {
        paintFrame = 0;
        paint();
      });
    };

    // Expensive: selects the variant (price / option sync) and drives the 3D
    // model. Runs once, when the value settles — never mid-drag — so a drag
    // can't thrash the variant or section render.
    const commit = () => {
      const radio = radios[indexFromRaw()];
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    // Snap to a carat's exact stop and commit it (eased slide, since not dragging).
    const settleTo = (index) => {
      slider.value = String(clampIndex(index) * SUBSTEPS);
      paintNow();
      commit();
    };

    slider.addEventListener('input', schedulePaint);

    // Direct manipulation follows the pointer with no easing while `.is-dragging`
    // is set; releasing restores the slide and snaps to the nearest carat.
    // Capturing the pointer keeps pointerup on the slider even when the finger
    // drifts off it, so the drag ends without a window listener.
    //
    // A tap on the track is NOT a drag: we hold off on `.is-dragging` until the
    // pointer travels past a small threshold, so the easing stays on and the
    // thumb glides to the tapped carat (the same animated slide as a scale-number
    // tap) instead of teleporting under the finger. The animation itself is the
    // existing transform-only transition, so this stays off the main thread.
    // Enough travel that a click's hand tremor never counts as a drag, but well
    // under any deliberate drag.
    const DRAG_THRESHOLD = 8;
    let pointerStartX = 0;
    let pointerStartY = 0;

    const onPointerMove = (event) => {
      if (dragging) return;
      if (Math.hypot(event.clientX - pointerStartX, event.clientY - pointerStartY) < DRAG_THRESHOLD) return;
      dragging = true;
      container.classList.add('is-dragging');
      paintNow();
    };
    const endPointer = (event) => {
      slider.removeEventListener('pointermove', onPointerMove);
      slider.releasePointerCapture?.(event.pointerId);
      container.classList.remove('is-dragging');
      dragging = false;
      settleTo(indexFromRaw());
    };
    slider.addEventListener('pointerdown', (event) => {
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
      dragging = false;
      slider.setPointerCapture?.(event.pointerId);
      slider.addEventListener('pointermove', onPointerMove);
    });
    slider.addEventListener('pointerup', endPointer);
    slider.addEventListener('pointercancel', endPointer);

    // Safety net for value changes that don't go through a pointer drag (e.g.
    // mouse wheel over the focused slider): snap + commit once it settles.
    slider.addEventListener('change', () => {
      if (dragging) return;
      settleTo(indexFromRaw());
    });

    // Keyboard moves a whole carat at a time (the sub-step grid is for dragging
    // only). Arrow direction mirrors in RTL to match the visual track.
    const keyStep = {
      ArrowUp: 1,
      ArrowDown: -1,
      PageUp: 1,
      PageDown: -1,
      ArrowRight: rtl ? -1 : 1,
      ArrowLeft: rtl ? 1 : -1
    };
    slider.addEventListener('keydown', (event) => {
      let next;
      if (event.key in keyStep) next = indexFromRaw() + keyStep[event.key];
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = max;
      else return;
      event.preventDefault();
      next = clampIndex(next);
      if (next !== indexFromRaw()) settleTo(next);
    });

    // Tapping a scale number jumps to that carat with the gentle animated slide.
    container.addEventListener('click', (event) => {
      const label = event.target.closest('[data-rtr-carat-to]');
      if (!label || !container.contains(label)) return;
      const index = Number(label.dataset.rtrCaratTo);
      if (Number.isNaN(index) || index === indexFromRaw()) return;
      settleTo(index);
    });

    paintNow();

    return {
      syncFromValue(value) {
        const index = radios.findIndex((radio) => normalize(radio.value) === normalize(value));
        if (index < 0 || index === indexFromRaw()) return;
        settleTo(index);
      }
    };
  };

  const initMenuScrollHandoff = () => {
    const info = document.querySelector('.product-page.main-product [id^="ProductInfo-"]');
    if (
      !info
      || info.dataset.rtrScrollHandoff === 'true'
      || (!document.body.classList.contains('dyo-product-page') && window.location.hash !== '#dyo')
    ) return;
    info.dataset.rtrScrollHandoff = 'true';

    let stickyTop = null;
    const getStickyTop = () => {
      if (stickyTop === null) stickyTop = Number.parseFloat(window.getComputedStyle(info).top) || 0;
      return stickyTop;
    };
    const getDistanceBeforeMenuPins = () => {
      if (
        window.innerWidth < 768
        || !document.body.classList.contains('dyo-product-page')
        || info.scrollTop > 1
      ) return 0;

      return Math.max(0, info.getBoundingClientRect().top - getStickyTop());
    };
    const normalizeWheelDelta = (event) => {
      if (event.deltaMode === 1) return event.deltaY * 16;
      if (event.deltaMode === 2) return event.deltaY * window.innerHeight;
      return event.deltaY;
    };
    const movePageThenMenu = (deltaY, distanceToStickyRest) => {
      const pageDelta = Math.min(deltaY, distanceToStickyRest);
      window.scrollBy({ top: pageDelta, behavior: 'auto' });

      const menuDelta = deltaY - pageDelta;
      if (menuDelta > 0) {
        window.requestAnimationFrame(() => {
          const previousMenuScroll = info.scrollTop;
          info.scrollTop += menuDelta;
          const unusedMenuDelta = menuDelta - (info.scrollTop - previousMenuScroll);

          if (unusedMenuDelta > 1) {
            window.scrollBy({ top: unusedMenuDelta, behavior: 'auto' });
          }
        });
      }
    };

    info.addEventListener('wheel', (event) => {
      const deltaY = normalizeWheelDelta(event);
      if (deltaY <= 0) return;

      const distanceToStickyRest = getDistanceBeforeMenuPins();
      if (distanceToStickyRest <= 1) return;

      event.preventDefault();
      movePageThenMenu(deltaY, distanceToStickyRest);
    }, { passive: false });

    let lastTouchY = 0;
    info.addEventListener('touchstart', (event) => {
      lastTouchY = event.touches[0]?.clientY || 0;
    }, { passive: true });

    info.addEventListener('touchmove', (event) => {
      const currentTouchY = event.touches[0]?.clientY || lastTouchY;
      const deltaY = lastTouchY - currentTouchY;
      lastTouchY = currentTouchY;
      if (deltaY <= 0) return;

      const distanceToStickyRest = getDistanceBeforeMenuPins();
      if (distanceToStickyRest <= 1) return;

      event.preventDefault();
      movePageThenMenu(deltaY, distanceToStickyRest);
    }, { passive: false });
  };

  const initSelectors = (root) => {
    if (root.dataset.rtrReady === 'true') return;
    root.dataset.rtrReady = 'true';
    const storedSelections = getStoredSelections();
    if (storedSelections['RTR Head Color']) root.dataset.rtrHeadTouched = 'true';

    const refreshMetalColors = (value) => {
      const latestSelections = getStoredSelections();
      const colors = getColorsForMetal(root, value);
      const platinum = isPlatinum(value);
      // A platinum shank has no alternative: the row stays, with Platinum alone.
      if (platinum) root.dataset.rtrRenderMetalRole = 'head';
      renderMetalColors(root, 'shank', platinum ? colors.slice(0, 1) : colors, platinum);
      renderMetalColors(root, 'head', colors);
      applyStoredSelections(
        root,
        latestSelections,
        platinum ? ['RTR Head Color'] : ['RTR Shank Color', 'RTR Head Color']
      );
      updateMetalColorAvailability(root);
      updateGroupLegends(root);
      syncSelectorMarkup(root);
    };

    if (storedSelections['RTR Metal Type']) {
      const storedMetalType = findMatchingInput(root, '[data-rtr-metal-type]', storedSelections['RTR Metal Type']);
      if (storedMetalType) {
        storedMetalType.checked = true;
        root.dataset.initialMetal = storedMetalType.value;
        syncVariantOption(root, storedMetalType.dataset.rtrSyncOption, storedMetalType.value);
      }
    }

    refreshMetalColors(root.querySelector('[data-rtr-metal-type]:checked')?.value || root.dataset.initialMetal);
    applyStoredSelections(root, storedSelections, ['RTR Stone Color', 'RTR Stone Shape', 'RTR Carat Size']);
    syncStoneColorTab(root);
    updateHeadForStone(root, root.querySelector('[data-rtr-stone-color]:checked')?.value);
    updateMetalColorAvailability(root);
    const caratSlider = initCaratSlider(root);
    const caratHandle = root.querySelector('[data-rtr-dyo-property="RTR Carat Size"]')?.dataset.rtrSyncOption || '';
    updateGroupLegends(root);

    syncMetalTypeSelection(root);
    const initialCarat = root.querySelector('[data-rtr-dyo-property="RTR Carat Size"]:checked');
    document.body.toggleAttribute('data-rtr-default-carat', initialCarat?.value === DEFAULT_MODEL_CARAT);
    if (initialCarat && caratHandle && !hasDyoStoneWeight()) {
      syncVariantOption(root, caratHandle, initialCarat.value);
    }

    const nativeMetal = metalOptionRoot(root);
    if (nativeMetal && root.querySelector('[data-rtr-metal-type]')) {
      nativeMetal.classList.add('rtr-dyo-native-option');
    }

    root.addEventListener('change', (event) => {
      const metalType = event.target.closest('[data-rtr-metal-type]');
      if (metalType) {
        syncVariantOption(root, metalType.dataset.rtrSyncOption, metalType.value);
        updateGroupLegends(root);
        persistSelections(root);
        return;
      }

      const input = event.target.closest('[data-rtr-dyo-property]');
      if (!input) return;

      if (input.dataset.rtrDyoProperty === 'RTR Carat Size') document.body.removeAttribute('data-rtr-default-carat');
      if (input.dataset.rtrMetalRole === 'head' && root.dataset.rtrAutoHead !== 'true') {
        root.dataset.rtrHeadTouched = 'true';
      }
      if (input.dataset.rtrMetalRole) root.dataset.rtrRenderMetalRole = input.dataset.rtrMetalRole;
      if (input.hasAttribute('data-rtr-stone-color')) updateHeadForStone(root, input.value);
      if (input.hasAttribute('data-rtr-stone-color')) syncStoneColorTab(root);
      updateMetalColorAvailability(root, input.dataset.rtrMetalRole || 'shank');
      syncVariantOption(root, input.dataset.rtrSyncOption, input.value);
      updateGroupLegends(root);
      syncSelectorMarkup(root);
      persistSelections(root);
    });

    const variantRoot = document.getElementById(`variant-update-${root.dataset.sectionId}`);
    variantRoot?.addEventListener('change', (event) => {
      const value = event.target.matches('select')
        ? event.target.options[event.target.selectedIndex]?.text || event.target.value
        : event.target.checked ? event.target.value : '';
      if (!value) return;

      if (caratSlider && caratHandle && event.target.closest(`[data-option-name="${CSS.escape(caratHandle)}"]`)) {
        caratSlider.syncFromValue(value);
        return;
      }
      if (!event.target.closest(`[data-option-name="${CSS.escape(root.dataset.metalOptionHandle)}"]`)) return;
      refreshMetalColors(value);
      syncMetalTypeSelection(root);
    });

    syncSelectorMarkup(root);
    persistSelections(root);
  };

  const initMedia = () => {
    const media = document.getElementById('rtr_show');
    const gallery = document.querySelector('[data-rtr-gallery]');
    if (!media || !gallery) {
      mediaState?.destroy();
      mediaState = null;
      return;
    }
    if (mediaState?.media === media && mediaState.gallery === gallery) return;

    mediaState?.destroy();

    const timers = new Set();
    const listeners = [];
    let destroyed = false;
    let attempts = 0;
    let frameRequested = false;

    const setTimer = (callback, delay) => {
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        if (!destroyed) callback();
      }, delay);
      timers.add(timer);
      return timer;
    };
    const addListener = (target, type, listener, options) => {
      target.addEventListener(type, listener, options);
      listeners.push(() => target.removeEventListener(type, listener, options));
    };

    const moveToVisibleSlot = () => {
      const slots = Array.from(gallery.querySelectorAll('[data-rtr-slot]'));
      const slot = slots.find((candidate) => candidate.getClientRects().length > 0) || slots[0];
      if (slot && media.parentElement !== slot) slot.appendChild(media);
      media.hidden = false;
    };

    moveToVisibleSlot();
    addListener(document, `eurus:media-gallery-ready:${gallery.dataset.rtrGallery}`, moveToVisibleSlot);
    addListener(window, 'resize', moveToVisibleSlot, { passive: true });

    const removeEmptyDuplicateFrames = () => {
      const frames = Array.from(media.querySelectorAll('iframe#fr_rtr_3_d'));
      if (frames.length < 2) return;

      const activeFrame = frames.find((frame) => frame.getAttribute('src')) || frames[0];
      frames.forEach((frame) => {
        if (frame !== activeFrame && !frame.getAttribute('src')) frame.remove();
      });
    };
    const bootstrap = () => {
      moveToVisibleSlot();
      if (media.querySelector('iframe') || document.getElementById('fr_rtr_3_d')) {
        return;
      }

      const rect = media.getBoundingClientRect();
      if (rect.width < 50 || rect.height < 50) {
        if (attempts++ < 40) setTimer(bootstrap, 250);
        return;
      }

      if (typeof window.cls_InovaDiamonds_Iframe?.Print_Button_Go1 === 'function') {
        if (frameRequested) return;
        frameRequested = true;
        try {
          window.cls_InovaDiamonds_Iframe.Print_Button_Go1();
        } catch (error) {
          frameRequested = false;
          console.warn('RTR model initialization failed.', error);
          if (attempts++ < 40) setTimer(bootstrap, 250);
          return;
        }
        setTimer(() => {
          removeEmptyDuplicateFrames();
          if (!media.querySelector('iframe') && !document.getElementById('fr_rtr_3_d')) {
            frameRequested = false;
            bootstrap();
          }
        }, 1500);
        return;
      }
      if (attempts++ < 40) setTimer(bootstrap, 250);
    };

    const vendorScript = document.getElementById('rtr-vto-script');
    if (vendorScript && typeof window.cls_InovaDiamonds_Iframe?.Print_Button_Go1 !== 'function') {
      addListener(vendorScript, 'load', bootstrap, { once: true });
    }
    bootstrap();

    mediaState = {
      media,
      gallery,
      destroy() {
        destroyed = true;
        timers.forEach((timer) => window.clearTimeout(timer));
        timers.clear();
        listeners.forEach((removeListener) => removeListener());
      }
    };
  };

  window.DYORTRcollectSelections = (scope) => collectSelections(scope || document);

  /* The CTA bar is position:fixed, so on its own it would float over the page footer.
     Tracking the footer's top against the viewport lets the bar dock just above the
     footer instead: it stays pinned to the bottom while the product is in view, rides
     up on top of the footer through the overlap zone, and leaves with the product once
     the footer takes over — so it never covers the footer. */
  const initCtaFooterDock = () => {
    const footer = document.querySelector('[class*="footer-group"]')
      || document.querySelector('[class*="footer"]')
      || document.querySelector('main')?.nextElementSibling;
    if (!footer || footer.dataset.rtrCtaDock === 'true') return;
    footer.dataset.rtrCtaDock = 'true';

    let ticking = false;
    const update = () => {
      ticking = false;
      const bar = document.querySelector('.dyo-cta');
      if (!bar) return;
      const overlap = window.innerHeight - footer.getBoundingClientRect().top;
      bar.style.bottom = overlap > 0 ? `${overlap}px` : '';
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  };

  // The ring-size row reuses the selector accordion, whose summary shows the
  // current choice; mirror the <select> value into it (the engraving row's
  // value is filled by the engraving store's syncHiddenInputs instead).
  const initRingSizeLegend = () => {
    if (document.body.dataset.rtrRingSizeLegend === 'true') return;
    document.body.dataset.rtrRingSizeLegend = 'true';

    const syncRingSizeDisplays = (value) => {
      document.querySelectorAll('[data-rtr-ring-size-display]').forEach((slot) => {
        slot.textContent = value || '';
      });
    };

    const getStoredRingSize = () => {
      try {
        return getStoredSetting().ringSize || window.localStorage.getItem('dyo_ring_size') || '';
      } catch (error) {
        return getStoredSetting().ringSize || '';
      }
    };

    const persistRingSize = (value) => {
      try {
        if (value) window.localStorage.setItem('dyo_ring_size', value);
        else window.localStorage.removeItem('dyo_ring_size');
      } catch (error) {
      }
      updateStoredSetting({ ringSize: value || '' });
    };

    const restoreRingSize = (select) => {
      if (!select) return;
      const storedRingSize = getStoredRingSize();
      if (!storedRingSize) return;

      const option = Array.from(select.options).find((item) => item.value === storedRingSize);
      if (!option) return;

      select.value = storedRingSize;
      syncRingSizeDisplays(storedRingSize);
      persistRingSize(storedRingSize);
    };

    document.querySelectorAll('select[name="properties[Ring Size]"]').forEach(restoreRingSize);

    document.addEventListener('change', (event) => {
      if (!event.target.matches('select[name="properties[Ring Size]"]')) return;
      syncRingSizeDisplays(event.target.value);
      persistRingSize(event.target.value || '');
    });

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches?.('select[name="properties[Ring Size]"]')) restoreRingSize(node);
          node.querySelectorAll?.('select[name="properties[Ring Size]"]').forEach(restoreRingSize);
          if (node.matches?.('[data-rtr-ring-size-display]') || node.querySelector?.('[data-rtr-ring-size-display]')) {
            syncRingSizeDisplays(getStoredRingSize());
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const init = () => {
    document.querySelectorAll('[data-rtr-dyo-selectors]').forEach(initSelectors);
    initMedia();
    initMenuScrollHandoff();
    initCtaFooterDock();
    initRingSizeLegend();
  };
  const handleSectionUnload = (event) => {
    if (!mediaState || !event.target.contains(mediaState.media)) return;
    mediaState.destroy();
    mediaState = null;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
})();
